import Vue from 'vue'
import VueHtmlToPaper from 'vue-html-to-paper'
import configUserApi from '@/api/config/_user.js'
import operationsApi from '@/api/comptabilite/_operations'

Vue.use(VueHtmlToPaper, { name: '_blank', specs: ['scrollbars=yes'], styles: [] })

// Dépense par la caisse, sur le modèle de la Remise : sortie immédiate, « Autorisé par » choisi dans la
// liste des paramètres, validation après coup par cette personne. Une référence unique par tentative
// garantit qu'un nouvel essai après coupure ne sort pas les espèces deux fois.
function newRequestId() {
    const bytes = new Uint8Array(16)
    if (!window.crypto || !window.crypto.getRandomValues) {
        throw new Error('La création d’une référence sécurisée est indisponible sur ce navigateur.')
    }
    window.crypto.getRandomValues(bytes)
    return 'dep-' + Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
}

function initialForm() {
    return { libelle: '', beneficiaire: '', montant: '', authorizer_id: null, commentaire: '' }
}

function responseMessage(error) {
    const body = error && error.response && error.response.data
    const errors = body && body.errors
    const first = errors && Object.values(errors)[0]
    if (first) return Array.isArray(first) ? first[0] : first
    return body && body.message ? body.message : ''
}

export default {
    name: 'CaisseDepense',
    props: {
        value: { type: Boolean, default: false },
        userId: { type: Number, required: true }
    },
    data() {
        return {
            loading: false, submitting: false, settingsEnabled: false, authorizers: [],
            availabilityMessage: '', error: '', step: 'form', requestId: '', frozenPayload: null,
            form: initialForm(), receipt: null
        }
    },
    computed: {
        visible() { return this.value },
        selectedAuthorizer() {
            return this.authorizers.find(user => Number(user.id) === Number(this.form.authorizer_id)) || null
        },
        canReview() {
            return this.settingsEnabled && this.authorizers.length > 0 && !this.loading && !this.submitting
        }
    },
    watch: {
        value(visible) { if (visible) this.prepare() }
    },
    methods: {
        async prepare() {
            this.loading = true
            this.settingsEnabled = false
            this.authorizers = []
            this.availabilityMessage = ''
            this.error = ''
            this.step = 'form'
            this.form = initialForm()
            this.receipt = null
            this.frozenPayload = null
            try {
                this.requestId = newRequestId()
                const setting = await configUserApi.getCashierExpenseSetting()
                if (!setting.success || !setting.data || !setting.data.enabled) {
                    this.availabilityMessage = 'Les dépenses par la caisse sont désactivées pour cet établissement.'
                    return
                }
                const list = await configUserApi.getCashierExpenseAuthorizers()
                if (!list.success || !Array.isArray(list.data)) throw new Error('Liste indisponible')
                this.authorizers = list.data.filter(user => Number(user.id) !== Number(this.userId))
                this.settingsEnabled = true
                if (!this.authorizers.length) {
                    this.availabilityMessage = 'Aucun autre utilisateur n’est habilité à autoriser une dépense caisse.'
                }
            } catch (error) {
                this.settingsEnabled = false
                this.availabilityMessage = error.message && error.message.includes('référence sécurisée')
                    ? error.message : 'Impossible de vérifier les autorisations. Aucune dépense ne peut être effectuée.'
            } finally {
                this.loading = false
            }
        },
        close() {
            if (this.submitting || this.step === 'uncertain') return
            this.$emit('input', false)
        },
        review() {
            this.error = ''
            if (!this.canReview) {
                this.error = this.availabilityMessage || 'Cette dépense ne peut pas être enregistrée actuellement.'
                return false
            }
            const amount = String(this.form.montant).trim()
            if (this.form.libelle.trim().length < 3) {
                this.error = 'Renseignez le motif de la dépense.'
                return false
            }
            if (!/^[1-9][0-9]*$/.test(amount) || !Number.isSafeInteger(Number(amount))) {
                this.error = 'Saisissez un montant entier strictement positif.'
                return false
            }
            if (!this.selectedAuthorizer || Number(this.form.authorizer_id) === Number(this.userId)) {
                this.error = 'Choisissez la personne habilitée qui a autorisé la dépense.'
                return false
            }
            this.step = 'review'
            return true
        },
        edit() {
            if (this.submitting || this.frozenPayload) return
            this.step = 'form'
        },
        payload() {
            return {
                request_id: this.requestId,
                libelle: this.form.libelle.trim(),
                beneficiaire: this.form.beneficiaire.trim(),
                montant: Number(String(this.form.montant).trim()),
                authorizer_id: Number(this.form.authorizer_id),
                commentaire: this.form.commentaire.trim()
            }
        },
        async pay() {
            if (this.submitting || (this.step !== 'review' && this.step !== 'uncertain')) return
            this.submitting = true
            this.error = ''
            if (!this.frozenPayload) this.frozenPayload = this.payload()
            try {
                const response = await operationsApi.payCashierExpense(this.frozenPayload)
                if (!response.success || !response.data || !response.data.reference) {
                    throw new Error('Réponse non confirmée.')
                }
                this.receipt = response.data
                this.step = 'receipt'
                this.$emit('paid', this.receipt)
            } catch (error) {
                const status = error && error.response && error.response.status
                const message = responseMessage(error)
                if (status === 422 || status === 403) {
                    // Refus explicite du serveur : rien n'a été sorti ; une nouvelle tentative aura sa propre référence.
                    this.step = 'form'
                    this.frozenPayload = null
                    this.requestId = newRequestId()
                    this.error = message || 'La dépense a été refusée. Vérifiez les informations et les autorisations.'
                } else if (status === 409) {
                    this.step = 'blocked'
                    this.error = message || 'Cette demande a déjà servi pour une autre dépense.'
                } else {
                    this.step = 'uncertain'
                    this.error = 'Enregistrement non confirmé. Ne créez pas une nouvelle dépense : réessayez cette même demande.'
                }
            } finally {
                this.submitting = false
            }
        },
        printReceipt() {
            if (this.receipt) this.$htmlToPaper('cashier-expense-receipt')
        },
        formatAmount(amount) {
            return Number(amount || 0).toLocaleString('fr-FR')
        }
    }
}
